import { cn } from '../lib/utils.js';

/**
 * メール成果物のプレビュー枠。
 *
 * **表示するのは必ずサーバーが組み立てた HTML そのもの**である。
 * ブラウザ側でブロック JSON から描き直すと「画面では正しいのに届いたメールが違う」
 * という最悪の事故が起きるため、この部品は文字列を受け取って表示するだけで、
 * 中身を解釈しない。
 *
 * `sandbox=""`（全許可を外す）で null origin にするので、srcdoc 内の
 * script / form / top-level navigation は動かない。顧客が生 HTML ブロックに
 * 何を書いても管理画面側には影響しない。
 */

export type MailPreviewDevice = 'desktop' | 'mobile';

export const MAIL_PREVIEW_DEVICE_WIDTH: Readonly<Record<MailPreviewDevice, number | null>> = {
  /** null = 枠いっぱい。 */
  desktop: null,
  /** iPhone SE 相当。実機で最も狭い部類。 */
  mobile: 375,
};

/**
 * 「画像を表示しない」面の再現。
 *
 * 受信側の画像ブロックは img を読み込ませないだけで領域は残るので、
 * `visibility:hidden` ではなく枠線付きの空箱に見せる。
 * **これは近似である**（実際の描画はクライアントごとに違う）ことを
 * 呼び出し側の注記に必ず書くこと。
 */
const IMAGES_OFF_STYLE =
  '<style data-mailsv="images-off">img{visibility:hidden!important}' +
  'img{outline:1px dashed #9993;outline-offset:-1px}</style>';

export interface MailPreviewFrameProps {
  /** サーバーが返した完成品の HTML。 */
  html: string;
  device?: MailPreviewDevice;
  /** 画像オフの面を見る。 */
  imagesOff?: boolean;
  /** スクリーンリーダー向けの名前。 */
  title?: string;
  className?: string;
  /** 枠の高さ。既定は縦長メールを想定した値。 */
  heightClassName?: string;
}

export function MailPreviewFrame({
  html,
  device = 'desktop',
  imagesOff = false,
  title = 'メールのプレビュー',
  className,
  heightClassName = 'h-[32rem]',
}: MailPreviewFrameProps) {
  // srcdoc の末尾に足した style は body 末尾の要素として解釈され、
  // 文書側の指定より後勝ちになる。HTML 本体は 1 バイトも書き換えない。
  const srcDoc = imagesOff ? `${html}${IMAGES_OFF_STYLE}` : html;
  const width = MAIL_PREVIEW_DEVICE_WIDTH[device];

  return (
    <div
      className={cn(
        'flex justify-center overflow-auto rounded-lg border border-border bg-panel p-3',
        className,
      )}
    >
      <iframe
        title={title}
        srcDoc={srcDoc}
        sandbox=""
        referrerPolicy="no-referrer"
        loading="lazy"
        className={cn('w-full rounded-md border border-border bg-white', heightClassName)}
        style={width === null ? undefined : { width, maxWidth: '100%', flex: '0 0 auto' }}
      />
    </div>
  );
}

/**
 * テキストパートのプレビュー。
 * HTML と別に必ず見せる（テキストだけが届く受信環境が実在する）。
 */
export function MailTextPreview({
  text,
  className,
  heightClassName = 'h-[32rem]',
}: {
  text: string;
  className?: string;
  heightClassName?: string;
}) {
  return (
    <pre
      className={cn(
        'overflow-auto whitespace-pre-wrap break-words rounded-lg border border-border bg-panel p-4 font-mono text-xs leading-relaxed text-foreground',
        heightClassName,
        className,
      )}
    >
      {text}
    </pre>
  );
}
